import React, { useEffect, useRef, useState } from "react";
import Styles from "./ConferenceCallPopup.module.scss";
import Typography from "@vahak/core-ui/dist/components/Typography";
import COLORS from "@vahak/core-ui/dist/constants/colors";
import IconWrapper from "@vahak/core-ui/dist/components/IconWrapper";
import { convertSecondsTime } from "../../methods/time";
import AppImg from "../utils/AppImg";
import { getImageNameFromURL } from "../../utils/getImageNameFromURL";
import Flex, { VerticalFlex } from "@vahak/core-ui/dist/layout/Flex";
import DraggableWrapper from "@vahak/core-ui/dist/components/common/DraggableWrapper/DraggableWrapper";
import AcceptIcon from "@vahak/core/dist/icons/call-accept.svg";
import MicIcon from "@vahak/core/dist/icons/call-mic.svg";
import MicMuteIcon from "@vahak/core/dist/icons/call-mic-muted.svg";
import RejectIcon from "@vahak/core/dist/icons/call-reject.svg";
import PlusIcon from "@vahak/core/dist/standard-icons/plus-circle-white.svg";
import { CallInfo } from "../../context/call/ClickToCallAppProvider";
import BordererCard from "@vahak/core-ui/dist/components/common/BordererCard/BordererCard";
import Container from "@vahak/core-ui/dist/layout/Container";
import TextCTA from "@vahak/core-ui/dist/components/TextCTA";
import Button from "@vahak/core-ui/dist/components/Button";
import { PLIVO_CALL_STATUS_TEXT } from "../../context/call/type";

interface ConferenceCallPopupProps {
    onHangup?: () => void;
    onReject?: () => void;
    onToggleMute?: () => void;
    onAnswer?: () => void;
    onClickAddToConference?: () => void;
    callInfo?: CallInfo;
    onKick?: (cuid: string) => void;
}

const ConferenceCallPopup = ({
    onHangup,
    onToggleMute,
    callInfo,
    onAnswer,
    onReject,
    onClickAddToConference,
    onKick
}: ConferenceCallPopupProps) => {
    const { startTime, endTime, isCallActive } = callInfo || {};
    const timerId = useRef<NodeJS.Timer | null>();
    const [duration, setDuration] = useState(0);
    const showMuteBtn = callInfo?.isConnected;

    const { m, s, h } = convertSecondsTime(duration);
    const hour = h.toString().padStart(2, "0");
    const min = m.toString().padStart(2, "0");
    const sec = s.toString().padStart(2, "0");

    const callDurationFormattedText = `${h ? `${hour}:` : ""}${min}:${sec}`;

    const updateDuration = () => {
        if (startTime) {
            const A = startTime.getTime();
            const B = endTime?.getTime?.() || Date.now();
            const duration = Math.ceil((B - A) / 1000);
            setDuration(duration);
        }
    };

    useEffect(() => {
        if (!startTime) return;

        const shouldTrack = isCallActive && !endTime;

        // Update once immediately
        updateDuration();

        if (shouldTrack) {
            timerId.current = setInterval(updateDuration, 1000);
        }

        // Cleanup function
        return () => {
            if (timerId.current) {
                clearInterval(timerId.current);
                timerId.current = null;
            }
        };
    }, [startTime, endTime, isCallActive]);
    const membersList = callInfo?.membersInfo ? Object.values(callInfo?.membersInfo) : [];

    return (
        <>
            <DraggableWrapper
                initialStyle={{
                    left: "50%",
                    top: "80%",
                    transform: "translateX(-50%)"
                }}
            >
                <div className={Styles.main}>
                    <VerticalFlex gap={12} justifyContent="center">
                        <Flex gap={12} justifyContent="center">
                            {membersList.map((member, index) => {
                                const allowKick =
                                    [PLIVO_CALL_STATUS_TEXT.ANSWERED].includes(
                                        member.remote_meta_info?.current_status!
                                    ) &&
                                    membersList?.length > 1 &&
                                    index > 0;

                                return (
                                    <>
                                        <BordererCard className={Styles.person} padded={false}>
                                            <AppImg
                                                src={getImageNameFromURL(member?.imgUrl || `default.png`)}
                                                alt={""}
                                                width={"40"}
                                                height={"40"}
                                                className={Styles.personImg}
                                            />
                                            <VerticalFlex gap={2}>
                                                <Typography weight="semibold" size="xs">
                                                    {member?.name}
                                                </Typography>
                                                {!!member?.remote_meta_info && (
                                                    <Typography
                                                        weight="semibold"
                                                        size="xs"
                                                        align="center"
                                                        textTransform="uppercase"
                                                    >
                                                        ({member?.remote_meta_info?.current_status})
                                                    </Typography>
                                                )}
                                                {!!onKick && allowKick && (
                                                    <Button
                                                        fillType="text"
                                                        blockBtn
                                                        color="danger"
                                                        size="s"
                                                        onClick={() => onKick(member.call_uuid)}
                                                    >
                                                        REMOVE
                                                    </Button>
                                                )}
                                            </VerticalFlex>
                                        </BordererCard>
                                    </>
                                );
                            })}
                        </Flex>

                        <div className={Styles.action}>
                            {showMuteBtn && (
                                <IconWrapper width={32} height={32} onClick={onToggleMute}>
                                    {callInfo?.isMuted ? <MicMuteIcon /> : <MicIcon />}
                                </IconWrapper>
                            )}
                            {!!(onHangup || onReject) && (
                                <IconWrapper width={32} height={32} onClick={onHangup ? onHangup : onReject}>
                                    {<RejectIcon />}
                                </IconWrapper>
                            )}

                            {!!onAnswer && (
                                <IconWrapper width={32} height={32} onClick={onAnswer}>
                                    {<AcceptIcon />}
                                </IconWrapper>
                            )}
                            {!!onClickAddToConference && (
                                <IconWrapper width={32} height={32} onClick={onClickAddToConference}>
                                    <PlusIcon />
                                </IconWrapper>
                            )}
                            <Flex gap={20} justifyContent="center">
                                {callInfo?.startTime ? (
                                    <Typography weight="bold" color={COLORS.RED} size="s">
                                        {callDurationFormattedText}
                                    </Typography>
                                ) : null}
                                <Typography size="s" color={COLORS.WHITE}>
                                    {callInfo?.callDesc}
                                </Typography>
                            </Flex>
                        </div>
                    </VerticalFlex>
                </div>
            </DraggableWrapper>
        </>
    );
};

export default ConferenceCallPopup;
